import React from 'react';
import { Trans } from '@lingui/macro';

import {
  InteractionFragment,
  PublicSetWithEWebinarFragment,
  RegisterType,
  usePublicSetQuery,
} from '$/GraphQL/schema/schema';
import {
  AttendeeCardProps,
  InteractionDisplayState,
} from '$/components/Interactions/InteractionInfo';
import Loading from '$/components/Loading';

import { withWebinarPlayerContext } from '$/components/WebinarPlayer/context';
import { PageMode } from '$/components/ui/Input/PageBuilder.types';
import { PlayerMode } from '$/components/WebinarPlayer/types';
import { logger } from '$/utils/logger';
import { useHasChangedShallow } from '$/hooks/useHasChanged';
import { LocalTimeZone } from '$/utils/LocalTimeZone';
import { useServerContext } from '$/hooks/useServerDateNow';
import { useEwebinarSession } from '$/hooks/useEwebinarSession';

import { NextWebinarItem } from './NextWebinarItem';
import CardMain from '../cards/CardMain';
import { useCardContext } from '../cards/CardContext';

const NextWebinarCardBody: React.FC<{
  nextSet: PublicSetWithEWebinarFragment;
}> = withWebinarPlayerContext(
  ['mode', 'set', 'ewebinar', 'attendee'],
  'NextWebinarCardBody'
)(({ nextSet, context }) => {
  const { interaction, displayState } = useCardContext();
  const { mode, set, ewebinar, attendee } = context;
  const state = {
    mode: mode === PlayerMode.edit ? PageMode.preview : PageMode.public,
    ewebinar,
    set,
  };

  if (
    state.mode === PageMode.public &&
    interaction.details?.registerType === RegisterType.AutoRegister &&
    !attendee?.email
  ) {
    interaction.details.registerType = RegisterType.PopupRegistrationForm;
  }

  switch (displayState) {
    case InteractionDisplayState.Result:
    case InteractionDisplayState.Submitted:
      return (
        <>
          <CardMain.SubmitBubble>
            <CardMain.IconThanks />
            <CardMain.Jumbotron className='px-2e'>
              <Trans>We sent you a confirmation email, see you soon!</Trans>
            </CardMain.Jumbotron>
          </CardMain.SubmitBubble>
        </>
      );
    case InteractionDisplayState.Normal:
      return (
        <NextWebinarItem
          set={nextSet}
          nextWebinar={nextSet.publicWebinar!}
          interaction={interaction}
          state={state}
          attendee={attendee}
        />
      );
  }
});

export const NextWebinarCard: React.FC<AttendeeCardProps> = withWebinarPlayerContext([
  'mode',
  'setInteractionState',
])(({ context: { mode, setInteractionState } }) => {
  const { setHasMedia, interaction } = useCardContext();
  const nextSetQuery = usePublicSetQuery({
    variables: { id: interaction.details?.setId ?? '0' },
    skip: !interaction.details?.setId,
    fetchPolicy: 'cache-first',
  });

  const nextSet = nextSetQuery.data?.publicSet;
  const nextWebinar = nextSet?.publicWebinar;
  const { serverDateNow } = useServerContext();
  const { sessions, isLoading: sessionsLoading } = useEwebinarSession({
    ewebinar: nextWebinar,
    timezone: LocalTimeZone.name,
    serverDateNow,
  });

  logger.debug('NextWebinarCard nextwebinar', {
    nextSetQuery,
    nextWebinar,
    setId: interaction.details?.setId,
  });

  const hasSessionChanged = useHasChangedShallow([
    nextWebinar,
    nextSetQuery.error?.message,
    sessions?.sessions,
  ]);

  React.useEffect(() => {
    if (mode === PlayerMode.edit || !hasSessionChanged || nextSetQuery.loading) return;
    if (!nextSetQuery.error?.message && sessionsLoading) return;

    setInteractionState((prev) => {
      return {
        ...prev,
        [interaction.shortId]: {
          set: nextSet,
          invalid: Boolean(nextSetQuery.error?.message || !sessions?.sessions?.length),
        },
      };
    });
  }, [
    sessions?.sessions?.length,
    hasSessionChanged,
    interaction.shortId,
    mode,
    nextSetQuery.error?.message,
    nextWebinar,
    setInteractionState,
    nextSet,
    nextSetQuery.loading,
    sessions,
    sessionsLoading,
  ]);

  if (nextSetQuery.error?.message) {
    return mode === PlayerMode.edit ? (
      <CardMain.StandardCard>
        <div className='flex w-full text-xl text-color-error justify-center'>
          Invalid webinar selected
        </div>
      </CardMain.StandardCard>
    ) : null;
  }

  if (interaction.details?.setId && !nextSetQuery.data?.publicSet) {
    return (
      <CardMain.StandardCard>
        <Loading query={nextSetQuery} local />
      </CardMain.StandardCard>
    );
  }

  if (!nextSet || !nextWebinar || !interaction.details) {
    return (
      <CardMain.StandardCard>
        <div className='flex w-full text-xl text-color-error justify-center'>
          No webinar selected
        </div>
      </CardMain.StandardCard>
    );
  }

  // Inject media into interaction and card context
  setHasMedia(Boolean(nextSet.socialMediaTags.mediaRef?.imageUrl));
  // Fixed: Cannot assign to read only property 'imageMediaRef' of object '#<Object>'
  const fragment = {
    ...interaction.fragment,
    details: {
      ...interaction.fragment.details,
      imageMediaRef: nextSet.socialMediaTags.mediaRef,
    },
  } as InteractionFragment;
  interaction.updateFragment(fragment);

  return (
    <CardMain.StandardCard noDescription>
      <NextWebinarCardBody nextSet={nextSet} />
    </CardMain.StandardCard>
  );
});
